/// <amd-module name="Artifacts/FeedSettings/components/AddUpstreamPanel" />
import { AddUpstreamForm } from "Artifacts/FeedSettings/components/AddUpstreamForm";
import { AddUpstreamStage, useAddUpstream } from "Artifacts/FeedSettings/hooks/AddUpstream";
import {
  AddUpstreamFormAction,
  AddUpstreamType,
  formDataToUpstreamSources,
} from "Artifacts/FeedSettings/hooks/AddUpstreamForm";
import { FeedSettingsActions, IFeedSettingsModuleState } from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings, IUpstreamSource } from "Artifacts/FeedSettings/types";
import { ComponentState } from "Artifacts/Root/Constants";
import { getCurrentCollectionName } from "Artifacts/Root/helpers/CollectionHelpers";
import { Feed, FeedView } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { connect } from "react-redux";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as StringUtils from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails, ListItem, ScrollableList } from "VSSUI/List";
import { Panel } from "VSSUI/Panel";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IWizardItem, Wizard, WizardItemState } from "VSSUI/Wizard";

interface ITypeListItem {
  title: string;
  description: string;
  icon: string;
  upstreamType: AddUpstreamType;
}

interface IAddUpstreamTypeSelectionProps {
  wizardItems: IWizardItem[];
  currentStage: AddUpstreamStage;
  setCurrentStage: (stage: AddUpstreamStage) => void;
  dispatch: React.Dispatch<AddUpstreamFormAction>;
  isOrgAadBacked: boolean;
}

const AddUpstreamTypeSelection: React.FC<IAddUpstreamTypeSelectionProps> = ({
  dispatch,
  setCurrentStage,
  wizardItems,
  currentStage,
  isOrgAadBacked,
}) => {
  const { pageContext } = useComponentContext();

  const upstreamTypes = [
    {
      title: Res.AddUpstreamPanel_PublicUpstream_Title,
      description: Res.AddUpstreamPanel_PublicUpstream_Description,
      icon: "Globe",
      upstreamType: AddUpstreamType.Public,
    },
    {
      title: Res.AddUpstreamPanel_SameCollectionFeed_Title,
      description: StringUtils.format(
        Res.AddUpstreamPanel_SameCollectionFeed_Description,
        getCurrentCollectionName(pageContext),
      ),
      icon: "EMI",
      upstreamType: AddUpstreamType.CollectionUpstream,
    },
  ];

  if (isOrgAadBacked) {
    upstreamTypes.push({
      title: Res.AddUpstreamPanel_CrossCollectionFeed_Title,
      description: Res.AddUpstreamPanel_CrossCollectionFeed_Description,
      icon: "CityNext",
      upstreamType: AddUpstreamType.AadUpstream,
    });
  }

  return (
    <Card className="add-upstream-panel-types">
      <ScrollableList
        onActivate={(_event, item) => {
          wizardItems[currentStage].itemState.value = WizardItemState.Complete;
          setCurrentStage(AddUpstreamStage.Configuration);
          dispatch({ type: "type", value: item.data.upstreamType });
        }}
        itemProvider={new ArrayItemProvider<ITypeListItem>(upstreamTypes)}
        renderRow={renderTypeSelection}
        singleClickActivation={true}
      />
    </Card>
  );
};

const Actions = {
  addUpstreams: FeedSettingsActions.addUpstreams,
  loadFeeds: FeedSettingsActions.loadFeeds,
  loadFeedViews: FeedSettingsActions.loadFeedViews,
  loadServiceConnections: FeedSettingsActions.loadServiceConnections,
};

export interface IAddUpstreamPanelProps {
  onDismiss: () => void;
  onSuccess: () => void;
  isPublicFeed: boolean;
}

const AddUpstreamPanel: React.FC<IAddUpstreamPanelProps &
  IAddUpstreamPanelMapStateToProps &
  typeof Actions> = ({
  onDismiss,
  onSuccess,
  addUpstreams,
  defaultPublicUpstreams,
  upstreams,
  loadFeeds,
  loadFeedViews,
  loadServiceConnections,
  serviceConnections,
  serviceConnectionsComponentState,
  sameCollectionFeedListComponentState,
  feeds,
  settings,
  feedViews,
  isPublicFeed,
}) => {
  const { pageContext } = useComponentContext();
  const {
    formState,
    wizardItems,
    dispatch,
    protocols,
    selectedIndex,
    setSelectedIndex,
    selections,
    feedItems,
    feedViewItems,
    publicUpstreams,
    errors,
    serviceConnectionItems,
    isServiceConnectionsFeatureEnabled,
    resetFormState,
  } = useAddUpstream(
    defaultPublicUpstreams,
    upstreams,
    loadFeeds,
    loadFeedViews,
    loadServiceConnections,
    isPublicFeed,
    feeds,
    feedViews,
    settings,
    serviceConnections,
  );
  const footerButtonProps = [];
  const goBackToTypeSelection = () => {
    setSelectedIndex(AddUpstreamStage.SelectType);
    resetFormState();
  };
  if (selectedIndex === AddUpstreamStage.Configuration) {
    footerButtonProps.push({
      text: Res.FeedSettings_BackButton_Label,
      onClick: () => goBackToTypeSelection(),
    });
  }
  footerButtonProps.push({ text: Res.FeedSettings_CancelButton_Label, onClick: onDismiss });
  footerButtonProps.push({
    text: Res.AddUpstreamPanel_Add,
    primary: true,
    disabled: Object.keys(errors).length > 0,
    onClick: () => {
      const sources = formDataToUpstreamSources(pageContext, formState);
      addUpstreams(sources);
      onSuccess();
    },
  });

  return (
    <Panel
      titleProps={{ text: Res.AddUpstreamPanel_Header }}
      onDismiss={onDismiss}
      footerButtonProps={footerButtonProps}
    >
      <div className="flex flex-column add-upstream-panel">
        <Wizard
          className="add-upstream-wizard"
          items={wizardItems}
          onItemClick={() => {
            if (selectedIndex === AddUpstreamStage.Configuration) {
              goBackToTypeSelection();
            }
          }}
          selectedIndex={selectedIndex}
        />
        {selectedIndex === AddUpstreamStage.SelectType && (
          <AddUpstreamTypeSelection
            currentStage={selectedIndex}
            dispatch={dispatch}
            setCurrentStage={setSelectedIndex}
            wizardItems={wizardItems}
            isOrgAadBacked={settings?.isOrgAadBacked || false}
          />
        )}
        {selectedIndex === AddUpstreamStage.Configuration && formState.type != null && (
          <AddUpstreamForm
            selections={selections}
            errors={errors}
            sameCollectionFeedListComponentState={sameCollectionFeedListComponentState}
            feeds={feedItems}
            existingUpstreams={upstreams}
            publicUpstreams={publicUpstreams}
            type={formState.type}
            dispatch={dispatch}
            formState={formState}
            protocols={protocols}
            feedViews={feedViewItems}
            serviceConnections={serviceConnectionItems}
            serviceConnectionsComponentState={serviceConnectionsComponentState}
            isServiceConnectionsFeatureEnabled={isServiceConnectionsFeatureEnabled}
          />
        )}
      </div>
    </Panel>
  );
};

interface IAddUpstreamPanelMapStateToProps {
  defaultPublicUpstreams?: IUpstreamSource[];
  upstreams?: IUpstreamSource[];
  sameCollectionFeedListComponentState: ComponentState;
  feeds?: Feed[];
  feedViews?: FeedView[];
  settings?: IFeedSettings;
  serviceConnections?: ServiceEndpoint[];
  serviceConnectionsComponentState: ComponentState;
}

const mapStateToProps = (state: IFeedSettingsModuleState): IAddUpstreamPanelMapStateToProps => {
  const hubState = state["feed-settings"].hubState;
  return {
    defaultPublicUpstreams: hubState.settings?.defaultPublicUpstreams,
    upstreams: hubState.settings?.upstreamSources,
    sameCollectionFeedListComponentState: hubState.sameCollectionFeedListComponentState,
    feeds: hubState.feeds,
    settings: hubState.settings,
    feedViews: hubState.feedViews,
    serviceConnections: hubState.serviceEndpoints,
    serviceConnectionsComponentState: hubState.serviceConnectionsComponentState,
  };
};

function renderTypeSelection(
  index: number,
  item: ITypeListItem,
  details: IListItemDetails<ITypeListItem>,
  key?: string,
): JSX.Element {
  return (
    <ListItem key={key || "list-item" + index} index={index} details={details}>
      <div className="flex flex-row upstream-type-item">
        <Icon className="upstream-type-icon" size={IconSize.large} iconName={item.icon} />
        <div className="flex flex-column">
          <span className="upstream-type-item-text">{item.title}</span>
          <span className="fontSizeMS font-size-ms secondary-text upstream-type-item-text">
            {item.description}
          </span>
        </div>
      </div>
    </ListItem>
  );
}

const ConnectedAddUpstreamPanel = connect(
  mapStateToProps,
  Actions,
)(AddUpstreamPanel);

export { ConnectedAddUpstreamPanel as AddUpstreamPanel };

